/**
 * Payload schemas for the os relay RPCs between the server and the local runner (harness to nine
 * B5c, P1 item 3, fourth batch): dictate, interact, capture, the app inventory push, the
 * composer's step lane and its app recipes. Server ↔ runner only, never forwarded to the browser;
 * the Swift sidecar never sees these frames.
 *
 * Each schema is written from what its producers send today (apps/server os-verbs-relay,
 * os-lane-relay and os-recipe-relay; apps/sandbox's os-hands handlers and inventory push), and
 * its field docs moved here from the hand-written types, which are now DERIVED from these schemas
 * (computer-use/os-hands-protocol.ts, os-lane-protocol.ts, os-recipe-protocol.ts): one author.
 *
 * Every consumer still field-picks what it reads (parseClickCandidates, pickGroundBundle, the
 * relays' pickResult, the inventory ingest); these schemas describe, they do not gate.
 */
import { z } from 'zod';
import { isRecipeProgram, type OsRecipeProgram } from '../computer-use/os-recipe-program.js';
import { LANE_HANDBACK_REASONS } from '../jev/lane.schema.js';

/** A rect in points (window frames, window-relative capture regions). */
const RectSchema = z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() });
/** A size in points or pixels. */
const SizeSchema = z.object({ w: z.number(), h: z.number() });
/** A normalized [0,1] WINDOW-space sub-rect. */
const NormRegionSchema = z.object({ nx0: z.number(), ny0: z.number(), nx1: z.number(), ny1: z.number() });

/** T4's window pin (the sidecar's `pinWindow`): the Dock's front window. */
export const OsRecipePinSchema = z.object({ windowId: z.number(), frame: RectSchema, title: z.string().optional() });

// ============================================================================
// The app inventory push (brain → server, no requestId)
// ============================================================================

/** Brain → server (OS_APP_INVENTORY): which registry apps exist on this Mac. apps/sandbox os-app-inventory-push.ts. */
export const OsAppInventoryFrameSchema = z.object({
  v: z.literal(1),
  /** Distinct bundle ids the brain probed — its bundled registry's app set. */
  checked: z.array(z.string()),
  /** The subset of `checked` installed on this Mac (Launch Services). */
  present: z.array(z.string()),
  /** What this RUNNER build can do beyond the wire's baseline, re-asserted on
   *  every push (so a runner rollback clears it on its next connect). Absent
   *  on an older runner. */
  features: z.array(z.string()).optional(),
});

// ============================================================================
// Dictate (server → brain → server)
// ============================================================================

/** Server → brain (OS_DICTATE_REQUEST): os-verbs-relay.service.ts, one of `bundleId` / `frontmost: true`. */
export const OsDictateRequestSchema = z.object({
  requestId: z.string(),
  bundleId: z.string().optional(), // eyes-resolved target app; absent ONLY beside frontmost: true
  frontmost: z.literal(true).optional(), // G5: the runner resolves the frontmost app (no active row = NO_TARGET)
  text: z.string(), // user-authored: verbatim dictation or canonical reply (may be '')
  submit: z.boolean(), // press Return after typing
});

/** Brain → server (OS_DICTATE_RESULT): apps/sandbox os-dictate-handler.ts (a refusal, a run, a timeout). */
export const OsDictateResultSchema = z.object({
  requestId: z.string(),
  ok: z.boolean(),
  chars: z.number().optional(),
  submitted: z.boolean().optional(),
  error: z.string().optional(),
  code: z.string().optional(),
  bundleId: z.string().optional(), // G5: the app the runner resolved from frontmost (absent when the server named it)
  appName: z.string().optional(), // G5: that app's display name, for the copy
  display: z.string().optional(), // T4: the Dock's position word, when the app is the Dock's front window and there is more than one display
});

// ============================================================================
// Interact (server → brain → server)
// ============================================================================

/** The belt actions the interact op executes (computer-use/os-hands-protocol.ts OsInteractAction). */
const OsInteractActionSchema = z.enum([
  'key', 'scroll', 'click', 'fill', 'menu', 'click_exact', 'click_pick', 'fill_exact', 'fill_pick',
  /** R1b's read (the sidecar's `menu2`): the one item the path names, never pressed. */
  'menu_state',
]);

/** One row of a `click_exact` CANDIDATES listing (at most 12; nothing was pressed). */
export const OsClickCandidateSchema = z.object({
  index: z.number(),
  label: z.string(),
  role: z.string(),
  tier: z.number(),
  secure: z.boolean(),
  web: z.boolean(),
});

/** Rung 3 (vision grounding): the window capture the sidecar attaches to a click/fill NO_MATCH. */
export const OsGroundBundleSchema = z.object({
  /** Capture bytes (ride the frame ONLY — never logged), mirroring
   *  OsCaptureResult's field of the same name. */
  imageBase64: z.string(),
  mimeType: z.string(), // 'image/png'
  windowId: z.number(), // CGWindowID the capture is bound to
  /** Window frame at capture time — GLOBAL top-left-origin POINTS. */
  windowFrame: RectSchema,
  /** Pixel dimensions of imageBase64 AFTER any downscale (the pixels-to-points
   *  scale factor is implicit: imagePixelSize / windowFrame size). */
  imagePixelSize: SizeSchema,
  /** Echoed back on a zoom capture: the normalized [0,1] WINDOW-space sub-rect
   *  the request's `groundRegion` asked for (absent on a full-window bundle). */
  region: NormRegionSchema.optional(),
});

/** Rung 3 (vision grounding): a server-grounded click/fill point (`at` on the interact wire). */
export const OsPointTargetSchema = z.object({
  nx: z.number(), // normalized [0,1] in WINDOW space (x)
  ny: z.number(), // normalized [0,1] in WINDOW space (y)
  windowId: z.number(), // CGWindowID the grounding bundle was captured from
  expectedSize: SizeSchema, // window size in POINTS at capture time
});

/**
 * Server → brain (OS_INTERACT_REQUEST): os-verbs-relay.service.ts, every field sent only when its
 * caller set it (capabilities interact, menu, click, fill, undo-last).
 */
export const OsInteractRequestSchema = z.object({
  requestId: z.string(),
  bundleId: z.string().optional(), // eyes-resolved target app; absent ONLY beside frontmost: true
  frontmost: z.literal(true).optional(), // G5: the runner resolves the frontmost app (no active row = NO_TARGET)
  action: OsInteractActionSchema,
  key: z.string().optional(), // action 'key': a chord like 'space', 'return', 'cmd+z', 'escape'
  direction: z.enum(['up', 'down']).optional(), // action 'scroll'
  amount: z.number().optional(), // action 'scroll': pixels (default 120)
  target: z.string().optional(), // action 'click'/'click_exact'/'fill'/'fill_exact': the user's words for the control/field, verbatim
  ordinal: z.number().optional(), // action 'click'/'click_exact'/'fill_exact': 1-based pick among equal matches ("second result")
  value: z.string().optional(), // action 'fill'/'fill_exact'/'fill_pick' (phase ACT, T7c): the user-authored text for the named field
  path: z.array(z.string()).optional(), // action 'menu' / 'menu_state' (phase ACT): full menu path segments, e.g. ['File', 'Export']
  item: z.string().optional(), // action 'menu' / 'menu_state' (phase ACT): the user's words for the menu item
  gesture: z.enum(['click', 'double', 'right']).optional(), // action 'click' (phase ACT): double/right upgrade the click; absent = single left. 'click' (the explicit single left) only on 'click_exact'
  walk: z.string().optional(), // action 'click_pick'/'fill_pick': the walk id a click_exact/fill_exact CANDIDATES listing (or a vet) minted
  index: z.number().optional(), // action 'click_pick'/'fill_pick': the listed candidate's index
  label: z.string().optional(), // action 'click_pick'/'fill_pick': the candidate's listed label; the live label must still read exactly this
  allowPixel: z.boolean().optional(), // action 'click'/'fill'/'click_exact' (ACT W2a): permit the sidecar's pixel sight-grounding fallback rung (VETTED on click_exact); absent/false = W1 resolver rungs only
  wantGround: z.boolean().optional(), // action 'click'/'fill'/'click_exact' (rung 3): when every local rung misses, attach an OsGroundBundle to the NO_MATCH (click_exact: ELEMENT_NOT_FOUND) result; absent = today's plain refusal
  groundRegion: NormRegionSchema.optional(), // action 'click'/'fill'/'click_exact' (rung 3 zoom pass): normalized [0,1] WINDOW-space sub-rect to capture at native resolution; skips the resolver rungs (they already missed this turn)
  at: OsPointTargetSchema.optional(), // action 'click'/'fill' (rung 3 at-dial): act at a server-grounded window point; skips resolution entirely
  vet: z.literal(true).optional(), // action 'click' (T7b, with `at`): a labelled control under the point answers a one-row CANDIDATES walk for click_pick and is NOT pressed; only a surface with nothing to name is pressed
  pinWindow: OsRecipePinSchema.optional(), // action 'click_exact' on a named app (T8e): the window the press must land in (the sidecar's T4 pin: act in it, raise it once, or refuse WINDOW_CHANGED)
});

/** Brain → server (OS_INTERACT_RESULT): apps/sandbox os-interact-handler.ts (a refusal, a dial's answer). */
export const OsInteractResultSchema = z.object({
  requestId: z.string(),
  ok: z.boolean(),
  action: z.string().optional(),
  landing: z.string().optional(),
  matchedLabel: z.string().optional(),
  verified: z.boolean().optional(),
  error: z.string().optional(),
  code: z.string().optional(),
  ground: OsGroundBundleSchema.optional(), // rung 3: fresh window capture riding a wantGround NO_MATCH (ride the frame ONLY — never logged)
  walk: z.string().optional(), // code CANDIDATES: the walk id a click_pick (or, off fill_exact, a fill_pick) sends back
  candidates: z.array(OsClickCandidateSchema).optional(), // code CANDIDATES: at most 12 rows; nothing was pressed or typed
  bundleId: z.string().optional(), // G5: the app the runner resolved from frontmost (absent when the server named it)
  appName: z.string().optional(), // G5: that app's display name, for the copy
  display: z.string().optional(), // T4: the Dock's position word, when the app is the Dock's front window and there is more than one display
  title: z.string().optional(), // action 'menu_state': the item's live title
  enabled: z.boolean().optional(), // action 'menu_state': the item is not greyed out
  marked: z.boolean().optional(), // action 'menu_state': the item carries a check mark
  count: z.number().optional(), // code AMBIGUOUS on 'menu' / 'menu_state': how many items answered the path (none was pressed)
});

// ============================================================================
// Capture (server → brain → server)
// ============================================================================

/** A pixel rect scoping a capture to part of the target window (phase SEE): window-relative points, top-left origin. */
export const OsCaptureRegionSchema = RectSchema;

/** Server → brain (OS_CAPTURE_REQUEST): os-verbs-relay.service.ts. */
export const OsCaptureRequestSchema = z.object({
  requestId: z.string(),
  bundleId: z.string().optional(), // eyes-resolved target app; frontmost when absent
  region: OsCaptureRegionSchema.optional(), // phase SEE: window-relative rect, top-left origin
});

/** Brain → server (OS_CAPTURE_RESULT): apps/sandbox os-capture-handler.ts. */
export const OsCaptureResultSchema = z.object({
  requestId: z.string(),
  ok: z.boolean(),
  imageBase64: z.string().optional(),
  mimeType: z.string().optional(),
  error: z.string().optional(),
  code: z.string().optional(),
});

// ============================================================================
// The composer's step lane (T8d)
// ============================================================================

/** Server → runner (OS_LANE_REQUEST): os-lane-relay.service.ts, one of `app` / `frontmost: true`. */
export const OsLaneRequestSchema = z.object({
  requestId: z.string(),
  laneId: z.string(),
  app: z.string().optional(),
  frontmost: z.literal(true).optional(),
  goal: z.string(),
  inputs: z.record(z.string(), z.string()).optional(),
  /** Typed for the runner, which reads it when it is a number; no server caller sends it today. */
  maxSteps: z.number().optional(),
});

/** Runner → server (OS_LANE_PROGRESS): one act's own narration line. It never carries a requestId. */
export const OsLaneProgressSchema = z.object({
  laneId: z.string(),
  line: z.string(),
});

/** Server → runner (OS_LANE_CANCEL): stop the stretch before its next act. */
export const OsLaneCancelSchema = z.object({
  laneId: z.string(),
});

/** Runner → server (OS_LANE_RESULT): apps/sandbox os-lane-handler.ts; `steps` and `text` ride every frame. */
export const OsLaneResultSchema = z.object({
  requestId: z.string(),
  ok: z.boolean(),
  outcome: z.enum(['done', 'handback']).optional(),
  reason: z.enum(LANE_HANDBACK_REASONS).optional(),
  steps: z.array(z.string()),
  risky: z.object({ label: z.string() }).optional(),
  bundleId: z.string().optional(),
  appName: z.string().optional(),
  display: z.string().optional(),
  windowTitle: z.string().optional(),
  text: z.string(),
  code: z.string().optional(),
  error: z.string().optional(),
});

// ============================================================================
// App recipes (T9)
// ============================================================================

/**
 * A server-sent recipe program. The structure is os-recipe-program.ts's OsRecipeProgram, checked
 * by the same isRecipeProgram the runner runs (an op this build does not know passes on purpose:
 * the runner's lint names it), so this never refuses a program the runner would accept.
 */
const OsRecipeProgramSchema = z.custom<OsRecipeProgram>((v) => isRecipeProgram(v), { message: 'not a recipe program' });

/** Server → runner (OS_RECIPE_REQUEST): os-recipe-relay.service.ts (`program` + `programHash` only to a runner with recipe_ops:2). */
export const OsRecipeRequestSchema = z.object({
  requestId: z.string(),
  recipeId: z.string(),
  params: z.record(z.string(), z.string()),
  frontmost: z.literal(true).optional(),
  pinWindow: OsRecipePinSchema.optional(),
  phase: z.literal('yes').optional(),
  /** The front window's title the read-back named; a yes re-checks it. */
  expectTitle: z.string().optional(),
  program: OsRecipeProgramSchema.optional(),
  programHash: z.string().optional(),
});

/** Server → runner (OS_RECIPE_CANCEL): stop before the next step. */
export const OsRecipeCancelSchema = z.object({
  requestId: z.string(),
});

/** Runner → server (OS_RECIPE_RESULT): apps/sandbox os-recipe-handler.ts; `steps` rides every frame. */
export const OsRecipeResultSchema = z.object({
  requestId: z.string(),
  ok: z.boolean(),
  code: z.string().optional(),
  error: z.string().optional(),
  verified: z.boolean().optional(),
  title: z.string().optional(),
  label: z.string().optional(),
  steps: z.array(z.string()),
  candidates: z.array(z.string()).optional(),
  already: z.string().optional(),
  memo: z.object({ bundleId: z.string(), expectTitle: z.string(), pinWindow: OsRecipePinSchema.optional() }).optional(),
  bundleId: z.string().optional(),
  appName: z.string().optional(),
});
